import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Save, RefreshCw, BookOpen } from "lucide-react";
import { Toaster, toast } from "sonner";

const LearningResourceDetail = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [resource, setResource] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchResource();
  }, []);

  const fetchResource = async () => {
    try {
      const response = await authAxios.get("/profile/learning-resource");
      const content = response.data.content || "";
      setResource(content);
      setEditedContent(content);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching learning resource:", error);
      setLoading(false);
    }
  };

  const handleReAnalyse = async () => {
    setGenerating(true);
    try {
      const response = await authAxios.post("/profile/learning-resource", {});
      const newContent = response.data.content;
      setResource(newContent);
      setEditedContent(newContent);
      setIsEditing(false);
      toast.success("Learning resource re-analysed!");
    } catch (error) {
      console.error("Error generating learning resource:", error);
      toast.error("Failed to re-analyse resource");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save edited content to backend
      await authAxios.put("/profile/learning-resource/content", { content: editedContent });
      setResource(editedContent);
      setIsEditing(false);
      toast.success("Changes saved!");
    } catch (error) {
      console.error("Error saving resource:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/profile")}
                className="text-white/60 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-divine-gold" />
                <div>
                  <h1 className="font-cinzel text-xl text-white">
                    Learning Resource: {user?.name} - Agent Eros Bio
                  </h1>
                  <p className="text-white/40 text-sm">Personalized relationship guidance</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <Button
                    onClick={() => {
                      setEditedContent(resource);
                      setIsEditing(false);
                    }}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="outline"
                    className="border-divine-gold text-divine-gold hover:bg-divine-gold/10"
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={handleReAnalyse}
                    disabled={generating}
                    className="bg-divine-red hover:bg-divine-red-light text-white font-cinzel"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                    {generating ? "Re-Analysing..." : "Re-Analyse"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="glass-gold rounded-xl p-8">
          {isEditing ? (
            <Textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="bg-white/5 border-white/10 text-white min-h-[600px] font-mono text-sm leading-relaxed"
            />
          ) : (
            <div className="prose prose-invert max-w-none">
              <div className="text-white/90 leading-relaxed whitespace-pre-wrap">
                {resource || "No content available. Click Re-Analyse to generate."}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LearningResourceDetail;
